import type { Brand } from './brand';
import type { Material, MaterialAllergen } from './material';

export interface StorageContainer {
  id: number;
  organizationId?: number;
  name: string;
  subType?: string;
  sizeOpen?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RecipeStorageContainer {
  id: number;
  recipeId: number;
  storageContainerId: number;
  storageContainer: StorageContainer;
  qty: number;
  required: boolean;
  flag?: string;
  notes?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface Tool {
  id: number;
  organizationId?: number;
  name: string;
  toolType?: string;
  sizeSpec?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RecipeTool {
  id: number;
  recipeId: number;
  toolId: number;
  tool: Tool;
  qty: number;
  required: boolean;
  flag?: string;
  notes?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** How long a stage keeps. Hours exist because a hot hold is measured in them
 * and a day count would put unsafe food on the pass (T-1004). */
export type ShelfLifeUnit = "HOURS" | "DAYS";

export interface RecipeShelfLife {
  id: number;
  recipeId: number;
  sortOrder: number;
  stage: string;
  storageMethod?: string;
  coolingInstructions?: string;
  labelingRequired: boolean;
  /** The typed rule, e.g. "End of service". Superseded by the pair below, and
   * still what a stage with no number prints. */
  usedByDate?: string;
  shelfLifeValue?: number | null;
  shelfLifeUnit?: ShelfLifeUnit | null;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * A line on a recipe. It names exactly one thing: an ingredient you buy
 * (`materialId`/`material`) or a recipe you make (`subRecipeId`/`subRecipe`) —
 * an Alfredo sauce inside a Fettuccine Alfredo. Exactly one of the two pairs is
 * filled in; the server will not store a line with both or neither (T-703).
 */
export interface RecipeIngredient {
  id: number;
  recipeId: number;
  materialId: number | null;
  subRecipeId: number | null;
  quantity: number;
  unit: string;
  sortOrder: number;
  isOptional: boolean;
  preparationNotes?: string;
  groupName?: string;
  material: (Material & { allergens?: MaterialAllergen[] }) | null;
  subRecipe?: SubRecipeRef | null;
}

/** Just enough of a sub-recipe to show a line without loading the recipe. */
export interface SubRecipeRef {
  id: number;
  name: string;
  recipeType: RecipeType;
  servings: number | null;
  yieldQuantity?: number | null;
  yieldUom?: string | null;
}

/** What a line names, and therefore where its name and cost come from. */
export function ingredientLineName(line: {
  material?: { name: string } | null;
  subRecipe?: { name: string } | null;
}): string {
  return line.material?.name ?? line.subRecipe?.name ?? "";
}

export interface RecipeAllergen {
  id: number;
  name: string;
  code: string;
  severity: string;
  sources: string[];
}

export interface RecipeScope {
  id: number;
  recipeId: number;
  scopeType: 'COMPANY' | 'BRAND' | 'REGION' | 'LOCATION';
  scopeId: string;
  isExcluded: boolean;
}

export type RecipeSource = 'org-wide' | 'region-share';

export interface RegionRecipe {
  id: number;
  name: string;
  slug: string;
  category?: string;
  imageUrl?: string;
  isPublished: boolean;
  isActive: boolean;
  source: RecipeSource;
  targets: 'all' | string[];
}

export interface RecipeFavourite {
  id: number;
  recipeId: number;
  organizationId: number;
  createdAt: string;
  recipe: Pick<Recipe, 'id' | 'name' | 'slug' | 'category' | 'imageUrl' | 'videoUrl' | 'isPublished' | 'prepTimeMinutes' | 'cookTimeMinutes'>;
}

export interface PopularRecipe
  extends Pick<Recipe, 'id' | 'name' | 'slug' | 'category' | 'imageUrl' | 'videoUrl' | 'isPublished' | 'prepTimeMinutes' | 'cookTimeMinutes'> {
  viewCount: number;
}

/**
 * What a recipe is for. A PREP recipe is a component — a dressing, a stock, a
 * dough — made to be used by other recipes; a PLATED recipe is a dish that
 * leaves the pass. Both live in one list; only PLATED recipes belong on a menu.
 */
export type RecipeType = 'PREP' | 'PLATED';

export interface Recipe {
  id: number;
  organizationId?: number;
  companyId: number;
  brandId?: number;
  materialId?: number;
  parentRecipeId?: number;
  name: string;
  slug: string;
  description?: string;
  recipeType: RecipeType;
  category?: string;
  subcategory?: string;
  prepTimeMinutes?: number;
  cookTimeMinutes?: number;
  restTimeMinutes?: number;
  servings?: number;
  servingSize?: string;
  /** The batch this recipe makes, and one serving out of it (T-706). */
  yieldQuantity?: number | null;
  yieldUom?: string | null;
  portionQuantity?: number | null;
  portionUom?: string | null;
  difficulty?: 'EASY' | 'MEDIUM' | 'HARD';
  instructions?: unknown[];
  tips?: string;
  labelingRequirements?: string;
  targetSellingPrice?: number | null;
  /** Only on list responses that asked for `include=cost`. */
  cost?: {
    totalRecipeCost: number;
    costPerPortion: number | null;
    /** False when some line has no cost, so the numbers are a minimum. */
    isComplete: boolean;
  };
  /**
   * The menu item that already links this plated recipe. Only on list responses
   * that asked for `include=menuItem`; null when no menu item uses it.
   */
  menuItem?: { id: number; name: string } | null;
  imageUrl?: string;
  avatarUrl?: string;
  videoUrl?: string;
  isPublished: boolean;
  isActive: boolean;
  version: number;
  isLatestVersion: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  publishedAt?: string;
  /**
   * Which of the publish requirements this recipe still fails, straight from
   * the API's rule module (T-704). The banner lists these rather than working
   * them out again in the browser, where the two answers drifted apart.
   */
  missingForPublish?: string[];
  createdById?: number;
  updatedById?: number;
  deletedById?: number;
  createdBy?: { id: number; firstName: string; lastName: string; email: string };
  updatedBy?: { id: number; firstName: string; lastName: string; email: string };
  company?: {
    id: number;
    name: string;
    slug: string;
  };
  brand?: Brand;
  ingredients?: RecipeIngredient[];
  allergens?: RecipeAllergen[];
  scopes?: RecipeScope[];
  _count?: {
    ingredients: number;
    menuItems: number;
  };
}
